import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { ref } from 'vue';

const timeLocale = ref('en');

const normalizeLocale = locale => {
  const normalized = String(locale || 'en').replace(/_/g, '-');
  return Intl.DateTimeFormat.supportedLocalesOf([normalized])[0] || 'en';
};

const toDate = time =>
  time instanceof Date ? time : fromUnixTime(Number(time));

const dateTimeOptions = dateFormat => {
  const monthToken = dateFormat.match(/[ML]+/)?.[0] || '';
  const yearToken = dateFormat.match(/y+/)?.[0] || '';
  const dayToken = dateFormat.match(/d+/)?.[0] || '';
  const weekdayToken = dateFormat.match(/E+/)?.[0] || '';

  return {
    ...(yearToken && { year: yearToken.length === 2 ? '2-digit' : 'numeric' }),
    ...(monthToken && {
      month: ['numeric', '2-digit', 'short', 'long'][
        Math.min(monthToken.length, 4) - 1
      ],
    }),
    ...(dayToken && { day: dayToken.length === 2 ? '2-digit' : 'numeric' }),
    ...(weekdayToken && {
      weekday: weekdayToken.length >= 4 ? 'long' : 'short',
    }),
    ...(dateFormat.includes('h') && { hour: 'numeric', hour12: true }),
    ...(dateFormat.includes('H') && { hour: 'numeric', hour12: false }),
    ...(dateFormat.includes('m') && { minute: '2-digit' }),
    ...(dateFormat.includes('s') && { second: '2-digit' }),
  };
};

const relativeTimeValue = date => {
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(seconds);

  if (absoluteSeconds < 60) return [seconds, 'second'];
  if (absoluteSeconds < 3600) return [Math.round(seconds / 60), 'minute'];
  if (absoluteSeconds < 86400) return [Math.round(seconds / 3600), 'hour'];
  if (absoluteSeconds < 2592000) return [Math.round(seconds / 86400), 'day'];
  if (absoluteSeconds < 31536000)
    return [Math.round(seconds / 2592000), 'month'];
  return [Math.round(seconds / 31536000), 'year'];
};

export const setTimeLocale = locale => {
  timeLocale.value = normalizeLocale(locale);
};

export const getTimeLocale = () => timeLocale.value;

export const formatDateTime = (date, options) =>
  new Intl.DateTimeFormat(timeLocale.value, options).format(date);

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const date = toDate(time);
  return timeLocale.value === 'en'
    ? format(date, dateFormat)
    : formatDateTime(date, dateTimeOptions(dateFormat));
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = toDate(time);
  const now = new Date();
  const messageDate = messageStamp(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return messageStamp(messageTime, 'LLL d y, h:mm a');
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'MMM d');
  return format(date, 'MMM d, yyyy');
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const date = toDate(time);
  if (timeLocale.value === 'en') {
    return formatDistanceToNow(date, { addSuffix: true });
  }

  const [value, unit] = relativeTimeValue(date);
  return new Intl.RelativeTimeFormat(timeLocale.value, {
    numeric: 'auto',
  }).format(value, unit);
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  return messageStamp(time, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  if (!timeLocale.value.startsWith('en')) return time;

  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
